// This file is part of the Smart Building Message Gateway.
//
//   |     __| _ _|   __|   _ \   \  |
//   |     _|    |   (     (   | |\/ |
//  ____| ___| ___| \___| \___/ _|  _|
//
// Copyright © 2025 LEICOM AG. All Rights Reserved.
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING
// BUT NOT LIMITED  TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
// NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
//
// @author      Christian Stauffer <christian.stauffer@leicom.ch>, et al.

import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { AiOutlineDisconnect } from "react-icons/ai";
import { BiSolidTerminal } from "react-icons/bi";
import { VscRemoteExplorer } from "react-icons/vsc";
import { PiPlugsConnectedFill } from "react-icons/pi";
import { TbWorldWww } from "react-icons/tb";


import './Remote.css'

function Remote() {

  const [consoleOpen, setConsoleOpen] = useState(false);
  const [webOpen, setWebOpen] = useState(false);

  const [selected, setSelected] = useState(null);

  const location = useLocation();
  const { token } = location.state || {};
  const apiKey = window.env.API_KEY;
  const api = window.env.API_URL;

  const select = async (e, name) => {

    setConsoleOpen(false);

    switch(name){
      case "console":
        setConsoleOpen(true);
        setWebOpen(false);
        break;
      case "web":
        setWebOpen(true);
        setConsoleOpen(false);
        break;
      default:
    }
    setSelected(name);
  }

  useEffect((e) => {
    select(e, "source");
  }, []);

  return (
      <div className="page">
          <div className="header">
              <VscRemoteExplorer className="menu-icon" />
              <h2>Remote Control</h2>
          </div>
          <nav className="submenu">
            <ul className='submenu'>
              <li className='submenu'>
                <button className={`submenu ${selected === "console" ? "selected" : ""}`} onClick={(e) => select(e, "console")}><BiSolidTerminal className="submenu-icon"/> Console</button>
              </li>
              <li className='submenu'>
                <button className={`submenu ${selected === "web" ? "selected" : ""}`} onClick={(e) => select(e, "web")}><TbWorldWww className="submenu-icon"/> Web</button>
              </li>
            </ul>
          </nav>
          {consoleOpen && 
            <Console
              api = {api}
              token = {token}
              apiKey = {apiKey}
          />}
          {webOpen && 
            <WebRequests
              api = {api}
              token = {token}
              apiKey = {apiKey}
          />}
      </div>
    );
}

//
// Console
//

function Console({api, token, apiKey}) {
  const [consoleClients, setConsoleClient] = useState([]);
  const [consoleOverlayOpen, setConsoleOverlayOpen] = useState(false);

  const [input, setInput] = useState("");
  const [inDone, setInDone] = useState(false);

  const [ws, setWs] = useState(null);
    const [client, setClient] = useState([]);

  const fetchCients = async () => {
    try {   

      const response = await fetch(`${api}/tunneling/console/clients`, {
        method: "GET",
        headers: {
          "X-Api-Key": `${apiKey}`,
          "Authorization": `${token}`
        },
      });
      if (response.ok) {
        const clients = await response.json()
         console.log(clients)
        if (clients == null){
          clients = JSON.stringify([])
        }
        console.log("Console Clients", clients)
        setConsoleClient(clients)
      } else {
        console.log("fetch console clients failed")
        // setError("Cannot fetch Tokens.");
      }

    } catch (err) {
      console.log("fetch console clients excepted", err)
      // setConsoleClient(JSON.stringify([]))
    }
  }

  useEffect(() => {
    fetchCients();
  }, [api, token]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      setInDone(true);
    } else if (e.key === 'Backspace') {
      setInput((prev) => prev.slice(0, -1));
    } else if (e.key.length === 1) {
      setInput((prev) => prev + e.key);
    }
  };

  const resetInput = (e) => {
    setInput("");
    setInDone(false);
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [input]);

  const connectConsole = async (e, client) => {
    setClient(client)
    console.log("open console", client)

    // setLoading(true);
    const socket = new WebSocket(`${api}/tunneling/console/connect?token=${token}&ID=${client.id}`);
    setWs(socket);

    // setLoading(false);

    openConsoleOverlay()

    // return () => {
    //   socket.close();
    // };
  };

  const closeConsoleOverlay = (e) => {
    if (ws) {
      ws.close()
    }
    console.log("close console")
    setConsoleOverlayOpen(false)
  };

  const openConsoleOverlay = (e) => {
    setConsoleOverlayOpen(true)
  }

  return (
      <div>
        {consoleOverlayOpen && 
          <ConnectOverlay
            close={closeConsoleOverlay}
            client={client}
            ws={ws}
            input={input}
            resetInput={resetInput}
            inputDone={inDone}
          />}
        <h2>Clients</h2>

        <div className="settings">
        <table>
          <thead><tr><td>ID</td><td>Customer ID</td><td>Hostname</td><td>Connected Since</td><td>Disconnected Since</td><td></td></tr></thead>
          <tbody>
            {consoleClients.map((client, index) => (
              <tr><td>{client.id}</td><td>{client.customerID}</td><td>{client.hostname}</td><td>{client.lastConnect}</td><td>{client.disconnectedSince}</td><td><button onClick={(e) => connectConsole(e, client)}><PiPlugsConnectedFill /></button></td></tr>  
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ConnectOverlay({close, client, ws, input, resetInput, inputDone}){

  const [messages, setMessages] = useState([]);
  const outputRef = useRef(null);

  useEffect(() => {
    if (!ws) {
      close();
      return;
    }
    resetInput();

    console.log("client: ", client)
    setMessages( () => [`${client.user}@${client.hostname} #`]);

    const handleMessage = (event) => {
      setMessages((prev) => [...prev, ` >> ${event.data}`]);
    };

    ws.addEventListener("message", handleMessage);

    return () => {
      ws.removeEventListener("message", handleMessage);
    };
  }, [ws]);

  const handleSend = () => {
    if (input.trim() === "") return;
    ws.send(input);
    setMessages((prev) => [...prev, `${client.user}@${client.hostname} # ${input}`]);
    console.log('console sent:', input);
  };

  useEffect(() => {
    outputRef.current?.scrollTo(0, outputRef.current.scrollHeight);
  }, [messages]);

  useEffect(() => {
    if (inputDone) {
      switch(input) {
      case "clear":
        setMessages( () => [`${client.user}@${client.hostname} #`]);
        resetInput();
      };
      try {
        handleSend();
      } catch(err) {
        console.log("error send cmd over ws");
        close();
      }
      resetInput();

      switch(input) {
      case "exit":
        setMessages( () => [`${client.user}@${client.hostname} #`]);
        close();
        resetInput();
      };
    }
  }, [inputDone]);

  useEffect(() => {
    setMessages((prev) => {
      if (prev.length === 0) return [`${client.user}@${client.hostname} #${input}`];
      const updated = [...prev];
      updated[updated.length - 1] = `${client.user}@${client.hostname} #${input}`;
      return updated;
    });
  }, [input]);

  return (
    <div className="bigoverlay">
      <div className="form-container">
        <h3>Console - {client.id}@{client.remoteIP}</h3>
        <div className="console"
          ref={outputRef}
          style={{
            flex: 1,
            padding: "8px",
            overflowY: "auto",
            background: "#000000ff",
            color: "#94c23d",
            width: "100%",
            height: "400px"
          }}>
          {messages.map((msg, idx) => (
            <div key={idx}>{msg}</div>
          ))}
        </div>
        {/* <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type command and press Enter"
        /> */}
        <button type="button" onClick={close}><AiOutlineDisconnect />Disconnect</button>
      </div>
    </div>
  )
}

//
// WebRequests
//

function WebRequests({api, token, apiKey}) {
  const [webReqClients, setWebClient] = useState([]);
  const [webOverlayOpen, setWebOverlayOpen] = useState(false);

  const [client, setClient] = useState([]);

  const fetchWebCients = async () => {
    try {   

      const response = await fetch(`${api}/tunneling/http/clients`, {
        method: "GET",
        headers: {
          "X-Api-Key": `${apiKey}`,
          "Authorization": `${token}`
        },
      });
      if (response.ok) {
        const clients = await response.json()
         console.log(clients)
        if (clients == null){
          clients = JSON.stringify([])
        }
        console.log("Web Clients", clients)
        setWebClient(clients)
      } else {
        console.log("fetch web clients failed")
        // setError("Cannot fetch Tokens.");
      }

    } catch (err) {
      console.log("fetch web clients excepted", err)
    }
  }

  useEffect(() => {
    fetchWebCients();
  }, [api, token]);

  const closeWebOverlay = (e) => {
  
    console.log("close web overlay")
    setWebOverlayOpen(false)
  };

  const openWebOverlay = (e) => {
    setWebOverlayOpen(true)
  }

   const connectWeb = async (e, client) => {
    setClient(client)
    console.log("open console", client)

    openWebOverlay()
  };

  return (
      <div>
        {webOverlayOpen && 
          <BrowserOverlay
            close={closeWebOverlay}
            client={client}
            api={api}
            token={token}
          />}
        <h2>Clients</h2>

        <div className="settings">
        <table>
          <thead><tr><td>ID</td><td>Customer ID</td><td>Hostname</td><td>Connected Since</td><td>Disconnected Since</td><td></td></tr></thead>
          <tbody>
            {webReqClients.map((client, index) => (
              <tr><td>{client.id}</td><td>{client.customerID}</td><td>{client.hostname}</td><td>{client.lastConnect}</td><td>{client.disconnectedSince}</td><td><button onClick={(e) => connectWeb(e, client)}><PiPlugsConnectedFill /></button></td></tr>  
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BrowserOverlay({close, client, api, token}){

  const url = `${api}/tunneling/http/${client.id}/?token=${token}`

  return (
    <div className="bigoverlay">
      <div className="form-container">
        <h3>Web - {client.id}@{client.remoteIP}</h3>
        <div style={{ width: '100%', height: '500px' }}>
          <iframe
            src= {url}
            title="Example Page"
            width="100%"
            height="100%"
            style={{ border: 'none' }}
          ></iframe>
        </div>
        <button type="button" onClick={close}><AiOutlineDisconnect />Close</button>
      </div>
    </div>
  )
}

export default Remote;
